import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_NEUTRALS, CHART_RAMP } from '../../design/tokens';
import { rechartsGrid, rechartsTooltip, rechartsXAxis, rechartsYAxis } from '../../design/chartDefaults';
import { useCurrency } from '../../context/CurrencyContext';

const monthHasData = (row) => {
  const income = Number(row?.income || 0);
  const expenses = Number(row?.expenses || 0);
  return income > 0 || expenses > 0;
};

const DashboardCashflowChart = ({ monthlyChart = [] }) => {
  const { formatAmount, currency } = useCurrency();

  const { chartData, monthsWithData } = useMemo(() => {
    const raw = monthlyChart || [];
    const withData = raw.filter(monthHasData);
    const data = raw.map((row) => {
      if (!monthHasData(row)) {
        return { ...row, income: null, expenses: null, surplus: null };
      }
      return row;
    });
    return { chartData: data, monthsWithData: withData.length };
  }, [monthlyChart]);

  const showChart = monthsWithData >= 2;

  const formatCompact = (v) => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1000) {
      return `${currency.symbol}${(n / 1000).toFixed(0)}k`;
    }
    return formatAmount(n, { maximumFractionDigits: 0 });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row || !monthHasData(row)) return null;
    const monthLabel = row.month_key
      ? new Date(`${row.month_key}-01`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      : row.month;

    return (
      <div style={{ ...rechartsTooltip.contentStyle, borderColor: CHART_RAMP[0] }}>
        <p className="text-label font-medium text-ink mb-2">{monthLabel}</p>
        <p className="text-body text-ink-muted">Income: {formatAmount(row.income)}</p>
        <p className="text-body text-ink-muted">Expenses: {formatAmount(row.expenses)}</p>
        <p className="text-body text-ink">Surplus: {formatAmount(row.surplus)}</p>
      </div>
    );
  };

  const surplusDot = (props) => {
    const { cx, cy, payload } = props;
    if (!payload || payload.surplus == null) return null;
    return <circle cx={cx} cy={cy} r={3} fill={CHART_RAMP[0]} strokeWidth={0} />;
  };

  return (
    <section className="mb-12" aria-label="Income vs spending">
      <h2 className="font-display text-h2 text-ink mb-4">Income vs. spending</h2>

      {!showChart ? (
        <div className="h-[200px] flex items-center justify-center border border-pine/30 rounded-lg">
          <p className="text-body text-ink-muted text-center px-4 max-w-md">
            Your chart will appear once you&apos;ve tracked 2+ months of data.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-label text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-pine" aria-hidden />
              Income
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-ink" aria-hidden />
              Expenses
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-5 border-t-2 border-dashed border-pine" aria-hidden />
              Surplus
            </span>
          </div>
          <div className="overflow-x-auto -mx-1 px-1">
            <div
              className="min-w-[280px] h-[200px] md:h-[200px]"
              style={{ minWidth: Math.max(320, chartData.length * 56) }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid {...rechartsGrid} vertical={false} />
                  <XAxis dataKey="month" {...rechartsXAxis} />
                  <YAxis {...rechartsYAxis} tickFormatter={formatCompact} width={48} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: CHART_NEUTRALS.boneRaised }} />
                  <Bar dataKey="income" name="Income" radius={[4, 4, 0, 0]} maxBarSize={28} fill={CHART_RAMP[0]} />
                  <Bar dataKey="expenses" name="Expenses" radius={[4, 4, 0, 0]} maxBarSize={28} fill={CHART_NEUTRALS.ink} />
                  <Line
                    type="monotone"
                    dataKey="surplus"
                    name="Surplus"
                    stroke={CHART_RAMP[0]}
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={surplusDot}
                    connectNulls={false}
                    activeDot={{ r: 4, fill: CHART_RAMP[0] }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default DashboardCashflowChart;
